import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Table,
  Badge,
  Button,
  Input,
  Select,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPost } from "../lib/api";

interface InferenceOverview {
  totalRequests: number;
  totalCost: number;
  avgCostPerRequest: number;
  cacheHitRate: number;
  avgLatencyMs: number;
  modelBreakdown: Record<string, { requests: number; cost: number; avgLatency: number }>;
}

interface InferenceModel {
  model: string;
  provider: string;
  requests: number;
  cost: number;
  avgLatency: number;
  errorRate: number;
  cacheHitRate: number;
}

interface InferenceTenant {
  tenantId: string;
  tenantName: string;
  requests: number;
  cost: number;
  topModel: string;
}

interface ModelReconciliation {
  fleet: {
    totalRequests: number;
    divergedRequests: number;
    divergenceRate: number;
    unresolvedTruthRequests: number;
    unresolvedTruthRate: number;
    truthSources: Record<string, number>;
    topPairs?: Array<{ requestedModel: string; actualModel: string; count: number }>;
  };
  tenants: Array<{
    tenantId: string;
    totalRequests: number;
    divergedRequests: number;
    divergenceRate: number;
    unresolvedTruthRequests: number;
    unresolvedTruthRate: number;
    truthSources: Record<string, number>;
    topPairs?: Array<{ requestedModel: string; actualModel: string; count: number }>;
  }>;
}

interface ModelShareDiagnostics {
  fleetOpusShare: number;
  fleetWarning: boolean;
  tenantWarnings: Array<{ tenantId: string; opusShare: number }>;
}

interface PlatformProviderSecret {
  id: string;
  provider: string;
  label: string;
  method: string;
  isActive: boolean;
  status: string;
  rateCardBinding: string | null;
  metadata: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
  lastValidatedAt: number | null;
  lastRotatedAt: number | null;
}

interface PlatformProviderField {
  id: string;
  label: string;
  kind: "api_key" | "url";
  placeholder: string;
  helper: string;
  required: boolean;
  secret?: boolean;
}

interface PlatformProviderCatalogEntry {
  id: string;
  label: string;
  description: string;
  fields: PlatformProviderField[];
}

const PIE_COLORS = ["#1A61ED", "#F2C94C", "#4CAF50", "#E53E3E", "#9B59B6"];

const tooltipStyle = {
  backgroundColor: "var(--c-surface)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  fontSize: 12,
};

export default function InferenceUsage() {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState("");
  const [label, setLabel] = useState("");
  const [method, setMethod] = useState("api_key");
  const [secret, setSecret] = useState("");
  const [providerMetadata, setProviderMetadata] = useState<Record<string, string>>({});
  const [rateCardBinding, setRateCardBinding] = useState("");
  const [targetSecretId, setTargetSecretId] = useState("");

  const {
    data: overview,
    isLoading: loadingOverview,
    error: errorOverview,
  } = useQuery<InferenceOverview>({
    queryKey: ["inference", "overview"],
    queryFn: async () => {
      const data = await apiGet<
        | InferenceOverview
        | {
            totalRequests?: number;
            totalCostUsd?: number;
          }
      >("/api/inference/overview");

      // Deployment mode returns a lightweight aggregate shape from inference-proxy.
      if ("totalCost" in data || "modelBreakdown" in data) {
        return data as InferenceOverview;
      }

      const totalRequests = Number(data.totalRequests ?? 0);
      const totalCost = Number(data.totalCostUsd ?? 0);

      return {
        totalRequests,
        totalCost,
        avgCostPerRequest: totalRequests > 0 ? totalCost / totalRequests : 0,
        cacheHitRate: 0,
        avgLatencyMs: 0,
        modelBreakdown: {},
      };
    },
  });

  const { data: models, isLoading: loadingModels } = useQuery<InferenceModel[]>({
    queryKey: ["inference", "models"],
    queryFn: async () => {
      const data = await apiGet<
        | InferenceModel[]
        | {
            models?: Array<{
              model: string;
              requests?: number;
              costUsd?: number;
            }>;
          }
      >("/api/inference/models");

      if (Array.isArray(data)) {
        return data;
      }

      return (data.models ?? []).map((row) => {
        const [provider, ...modelParts] = row.model.split("/");
        const model = modelParts.length > 0 ? modelParts.join("/") : row.model;

        return {
          model,
          provider: modelParts.length > 0 ? provider : "managed",
          requests: Number(row.requests ?? 0),
          cost: Number(row.costUsd ?? 0),
          avgLatency: 0,
          errorRate: 0,
          cacheHitRate: 0,
        };
      });
    },
  });

  const { data: tenants } = useQuery<InferenceTenant[]>({
    queryKey: ["inference", "tenants"],
    queryFn: async () => {
      const data = await apiGet<
        | InferenceTenant[]
        | {
            tenants?: Array<{
              tenantId: string;
              tenantName?: string;
              requests?: number;
              costUsd?: number;
              topModel?: string;
            }>;
          }
      >("/api/inference/tenants");

      if (Array.isArray(data)) {
        return data;
      }

      return (data.tenants ?? []).map((row) => ({
        tenantId: row.tenantId,
        tenantName: row.tenantName ?? row.tenantId,
        requests: Number(row.requests ?? 0),
        cost: Number(row.costUsd ?? 0),
        topModel: row.topModel ?? "n/a",
      }));
    },
  });

  const { data: reconciliation } = useQuery<ModelReconciliation>({
    queryKey: ["inference", "model-reconciliation"],
    queryFn: async () => await apiGet<ModelReconciliation>("/api/inference/model-reconciliation"),
  });

  const { data: modelShare } = useQuery<ModelShareDiagnostics>({
    queryKey: ["inference", "model-share"],
    queryFn: async () => await apiGet<ModelShareDiagnostics>("/api/inference/model-share"),
  });

  const { data: platformSecrets } = useQuery<{ secrets: PlatformProviderSecret[] }>({
    queryKey: ["platform", "provider-secrets"],
    queryFn: () => apiGet<{ secrets: PlatformProviderSecret[] }>("/api/platform/provider-secrets"),
  });

  const { data: providerCatalog } = useQuery<{ providers: PlatformProviderCatalogEntry[] }>({
    queryKey: ["platform", "provider-catalog"],
    queryFn: () =>
      apiGet<{ providers: PlatformProviderCatalogEntry[] }>("/api/platform/provider-catalog"),
  });

  const selectedSecret = useMemo(
    () => (platformSecrets?.secrets ?? []).find((entry) => entry.id === targetSecretId),
    [platformSecrets?.secrets, targetSecretId],
  );
  const effectiveProvider = targetSecretId ? (selectedSecret?.provider ?? "") : provider;
  const selectedProvider = useMemo(
    () => (providerCatalog?.providers ?? []).find((entry) => entry.id === effectiveProvider),
    [providerCatalog?.providers, effectiveProvider],
  );
  const secretField = selectedProvider?.fields.find((field) => field.kind === "api_key");
  const metadataFields =
    selectedProvider?.fields.filter((field) => field.id !== secretField?.id) ?? [];
  const missingRequiredMetadata = metadataFields.some(
    (field) => field.required && !providerMetadata[field.id]?.trim(),
  );
  const providerOptions = useMemo(
    () => [
      { value: "", label: "Select provider" },
      ...((providerCatalog?.providers ?? []).map((entry) => ({
        value: entry.id,
        label: entry.label,
      })) as Array<{ value: string; label: string }>),
    ],
    [providerCatalog?.providers],
  );

  const saveSecretMutation = useMutation({
    mutationFn: async () => {
      const metadata = Object.fromEntries(
        Object.entries(providerMetadata).filter(([, value]) => value.trim()),
      );
      const body = {
        provider: effectiveProvider,
        label,
        method,
        secret,
        metadata,
        rateCardBinding: rateCardBinding || undefined,
      };
      if (targetSecretId) {
        return apiPost(`/api/platform/provider-secrets/${targetSecretId}/rotate`, {
          secret,
          metadata,
        });
      }
      return apiPost("/api/platform/provider-secrets", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform", "provider-secrets"] });
      setLabel("");
      setSecret("");
      setProviderMetadata({});
      setRateCardBinding("");
      setTargetSecretId("");
    },
  });

  const validateSecretMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/api/platform/provider-secrets/${id}/validate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform", "provider-secrets"] });
    },
  });

  const disableSecretMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/api/platform/provider-secrets/${id}/disable`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform", "provider-secrets"] });
    },
  });

  usePolling(
    [
      ["inference", "overview"],
      ["inference", "models"],
      ["inference", "tenants"],
      ["inference", "model-reconciliation"],
      ["inference", "model-share"],
      ["platform", "provider-catalog"],
      ["platform", "provider-secrets"],
    ],
    30_000,
  );

  // Pie chart data from model breakdown
  const pieData = useMemo(() => {
    if (!overview?.modelBreakdown) return [];
    return Object.entries(overview.modelBreakdown)
      .map(([model, data]) => ({
        name: model,
        value: Math.round(data.cost * 100) / 100,
      }))
      .sort((a, b) => b.value - a.value);
  }, [overview]);

  // Models sorted by cost descending
  const sortedModels = useMemo(() => {
    if (!models) return [];
    return [...models].sort((a, b) => b.cost - a.cost);
  }, [models]);

  // Tenants sorted by cost descending
  const sortedTenants = useMemo(() => {
    if (!tenants) return [];
    return [...tenants].sort((a, b) => b.cost - a.cost);
  }, [tenants]);

  const reconciliationTenants = useMemo(() => {
    if (!reconciliation?.tenants) return [];
    return [...reconciliation.tenants].sort((a, b) => b.divergenceRate - a.divergenceRate);
  }, [reconciliation]);

  const topDivergencePairs = useMemo(
    () =>
      (reconciliation?.fleet?.topPairs ?? []).map((pair) => ({
        ...pair,
        pairId: `${pair.requestedModel}->${pair.actualModel}`,
      })),
    [reconciliation],
  );

  const isLoading = loadingOverview || loadingModels;

  if (isLoading && !overview) {
    return (
      <div>
        <PageHeader title="Inference Usage" subtitle="Model routing and cost tracking" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (errorOverview && !overview) {
    return (
      <div>
        <PageHeader title="Inference Usage" subtitle="Model routing and cost tracking" />
        <QueryError
          error={errorOverview}
          queryKeys={[
            ["inference", "overview"],
            ["inference", "models"],
            ["inference", "tenants"],
            ["inference", "model-reconciliation"],
            ["inference", "model-share"],
          ]}
        />
      </div>
    );
  }

  const modelColumns = [
    {
      key: "model",
      header: "Model",
      render: (row: InferenceModel) => (
        <span className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
          {row.model}
        </span>
      ),
    },
    {
      key: "provider",
      header: "Provider",
      render: (row: InferenceModel) => <Badge color="gray">{row.provider}</Badge>,
    },
    {
      key: "requests",
      header: "Requests",
      render: (row: InferenceModel) => row.requests.toLocaleString(),
    },
    {
      key: "cost",
      header: "Cost",
      render: (row: InferenceModel) => (
        <span className="font-mono text-sm" style={{ color: "var(--c-text)" }}>
          ${row.cost.toLocaleString()}
        </span>
      ),
    },
    {
      key: "avgLatency",
      header: "Avg Latency (ms)",
      render: (row: InferenceModel) => (
        <span className="font-mono text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {row.avgLatency.toLocaleString()}
        </span>
      ),
    },
    {
      key: "errorRate",
      header: "Error Rate",
      render: (row: InferenceModel) => (
        <span
          className="font-mono text-xs"
          style={{ color: row.errorRate > 0.005 ? "var(--c-danger)" : "var(--c-text-secondary)" }}
        >
          {(row.errorRate * 100).toFixed(2)}%
        </span>
      ),
    },
    {
      key: "cacheHitRate",
      header: "Cache Hit Rate",
      render: (row: InferenceModel) => (
        <span className="font-mono text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {(row.cacheHitRate * 100).toFixed(0)}%
        </span>
      ),
    },
  ];

  const tenantColumns = [
    {
      key: "tenantName",
      header: "Tenant",
      render: (row: InferenceTenant) => (
        <span className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
          {row.tenantName}
        </span>
      ),
    },
    {
      key: "requests",
      header: "Requests",
      render: (row: InferenceTenant) => row.requests.toLocaleString(),
    },
    {
      key: "cost",
      header: "Cost",
      render: (row: InferenceTenant) => (
        <span className="font-mono text-sm" style={{ color: "var(--c-text)" }}>
          ${row.cost.toLocaleString()}
        </span>
      ),
    },
    {
      key: "topModel",
      header: "Top Model",
      render: (row: InferenceTenant) => <Badge color="blue">{row.topModel}</Badge>,
    },
  ];

  return (
    <div>
      <PageHeader title="Inference Usage" subtitle="Model routing and cost tracking" />

      <Card className="mb-8">
        <SectionHeader
          title="Source of truth"
          subtitle="This page blends routing analytics with control-plane operator controls"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-medium mb-1" style={{ color: "var(--c-text)" }}>
              Routing and model truth
            </div>
            <div style={{ color: "var(--c-text-secondary)" }}>
              Sourced from the control-plane runtime ledger and requested vs actual model
              reconciliation.
            </div>
          </div>
          <div>
            <div className="font-medium mb-1" style={{ color: "var(--c-text)" }}>
              Tenant spend on this page
            </div>
            <div style={{ color: "var(--c-text-secondary)" }}>
              Shows inference spend and model mix only. Full tenant economics and margin stay on
              Usage Analytics and Tenant Detail.
            </div>
          </div>
          <div>
            <div className="font-medium mb-1" style={{ color: "var(--c-text)" }}>
              Bundled provider credentials
            </div>
            <div style={{ color: "var(--c-text-secondary)" }}>
              Managed by Adam/control-plane and kept separate from tenant BYO or subscription
              credentials.
            </div>
          </div>
        </div>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Requests" value={(overview?.totalRequests ?? 0).toLocaleString()} />
        <StatCard title="Total Cost" value={"$" + (overview?.totalCost ?? 0).toLocaleString()} />
        <StatCard
          title="Avg Cost / Request"
          value={"$" + (overview?.avgCostPerRequest ?? 0).toFixed(3)}
        />
        <StatCard
          title="Cache Hit Rate"
          value={((overview?.cacheHitRate ?? 0) * 100).toFixed(0) + "%"}
          color="var(--c-success)"
        />
        <StatCard
          title="Routing Divergence"
          value={((reconciliation?.fleet?.divergenceRate ?? 0) * 100).toFixed(1) + "%"}
          color={
            (reconciliation?.fleet?.divergenceRate ?? 0) > 0.02
              ? "var(--c-warning)"
              : "var(--c-success)"
          }
        />
        <StatCard
          title="Unresolved Truth"
          value={((reconciliation?.fleet?.unresolvedTruthRate ?? 0) * 100).toFixed(1) + "%"}
          color={
            (reconciliation?.fleet?.unresolvedTruthRate ?? 0) > 0.01
              ? "var(--c-danger)"
              : "var(--c-success)"
          }
        />
        <StatCard
          title="Fleet Opus Share"
          value={((modelShare?.fleetOpusShare ?? 0) * 100).toFixed(1) + "%"}
          color={modelShare?.fleetWarning ? "var(--c-warning)" : "var(--c-success)"}
        />
        <StatCard
          title="Truth Source"
          value={
            Object.entries(reconciliation?.fleet?.truthSources ?? {}).sort(
              (a, b) => Number(b[1]) - Number(a[1]),
            )[0]?.[0] ?? "n/a"
          }
        />
      </div>

      <SectionHeader
        title="Platform Provider Secrets"
        subtitle="Bundled provider credentials owned by Adam/control-plane"
      />
      <Card className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Select
            label="Provider"
            value={effectiveProvider}
            onChange={(e) => {
              setTargetSecretId("");
              setProvider(e.target.value);
              setProviderMetadata({});
            }}
            options={providerOptions}
          />
          <Select
            label="Action"
            value={targetSecretId}
            onChange={(e) => {
              const nextId = e.target.value;
              setTargetSecretId(nextId);
              const nextSecret = (platformSecrets?.secrets ?? []).find(
                (entry) => entry.id === nextId,
              );
              setProvider(nextSecret?.provider ?? "");
              setProviderMetadata(
                Object.fromEntries(
                  Object.entries(nextSecret?.metadata ?? {}).flatMap(([key, value]) =>
                    typeof value === "string" ? [[key, value]] : [],
                  ),
                ),
              );
            }}
            options={[
              { value: "", label: "Create new secret" },
              ...((platformSecrets?.secrets ?? []).map((entry) => ({
                value: entry.id,
                label: `Rotate: ${entry.provider} / ${entry.label}`,
              })) as Array<{ value: string; label: string }>),
            ]}
          />
          <Input
            label="Label"
            placeholder={
              selectedProvider
                ? `Primary ${selectedProvider.label} bundled key`
                : "Primary bundled provider key"
            }
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Select
            label="Method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            options={[
              { value: "api_key", label: "API Key" },
              { value: "token", label: "Token" },
            ]}
          />
          <Input
            label="Rate Card Binding"
            placeholder={effectiveProvider ? `${effectiveProvider}-current` : "provider-rate-card"}
            value={rateCardBinding}
            onChange={(e) => setRateCardBinding(e.target.value)}
          />
          <Input
            label={
              targetSecretId
                ? `Replacement ${secretField?.label ?? "secret"}`
                : (secretField?.label ?? "Secret")
            }
            type="password"
            placeholder={secretField?.placeholder ?? "Provider secret"}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
          {metadataFields.map((field) => (
            <Input
              key={field.id}
              label={field.label}
              type={field.secret ? "password" : field.kind === "url" ? "url" : "text"}
              placeholder={field.placeholder}
              value={providerMetadata[field.id] ?? ""}
              onChange={(e) =>
                setProviderMetadata((current) => ({
                  ...current,
                  [field.id]: e.target.value,
                }))
              }
            />
          ))}
        </div>
        <div className="flex justify-end gap-2 mb-4">
          <Button
            variant="primary"
            onClick={() => saveSecretMutation.mutate()}
            disabled={
              !effectiveProvider ||
              !secret.trim() ||
              missingRequiredMetadata ||
              (!targetSecretId && !label.trim()) ||
              saveSecretMutation.isPending
            }
          >
            {saveSecretMutation.isPending
              ? "Saving..."
              : targetSecretId
                ? "Rotate Secret"
                : "Create Secret"}
          </Button>
        </div>
        <Table
          columns={[
            { key: "provider", header: "Provider" },
            { key: "label", header: "Label" },
            {
              key: "status",
              header: "Status",
              render: (row: PlatformProviderSecret) => (
                <Badge
                  color={
                    row.status === "validated" ? "green" : row.status === "revoked" ? "red" : "gray"
                  }
                >
                  {row.status}
                </Badge>
              ),
            },
            {
              key: "isActive",
              header: "Active",
              render: (row: PlatformProviderSecret) => (
                <Badge color={row.isActive ? "green" : "secondary"}>
                  {row.isActive ? "Yes" : "No"}
                </Badge>
              ),
            },
            {
              key: "lastValidatedAt",
              header: "Last Validated",
              render: (row: PlatformProviderSecret) =>
                row.lastValidatedAt ? new Date(row.lastValidatedAt).toLocaleString() : "Never",
            },
            {
              key: "actions",
              header: "Actions",
              render: (row: PlatformProviderSecret) => (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => validateSecretMutation.mutate(row.id)}
                    disabled={validateSecretMutation.isPending}
                  >
                    Validate
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => disableSecretMutation.mutate(row.id)}
                    disabled={disableSecretMutation.isPending || !row.isActive}
                  >
                    Disable
                  </Button>
                </div>
              ),
            },
          ]}
          data={(platformSecrets?.secrets ?? []) as unknown as Record<string, unknown>[]}
          keyField="id"
          emptyMessage="No platform provider secrets"
        />
      </Card>

      {/* Model cost breakdown pie chart */}
      <SectionHeader
        title="Cost by Model"
        subtitle="Distribution of inference costs across models"
      />
      <Card className="mb-8">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              outerRadius={100}
              innerRadius={50}
              dataKey="value"
              nameKey="name"
              label={({ name, percent }: { name: string; percent: number }) =>
                `${name} (${(percent * 100).toFixed(0)}%)`
              }
              labelLine={{ stroke: "var(--c-text-faint)" }}
            >
              {pieData.map((_entry, index) => (
                <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={tooltipStyle}
              formatter={(value: number) => [`$${value.toLocaleString()}`, "Cost"]}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: "var(--c-text-secondary)" }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      {/* Models table */}
      <SectionHeader
        title="Models"
        subtitle={`${sortedModels.length} model${sortedModels.length !== 1 ? "s" : ""} in use`}
      />
      <Card padding={false} className="mb-8">
        <Table
          columns={modelColumns}
          data={sortedModels as unknown as Record<string, unknown>[]}
          keyField="model"
          loading={loadingModels}
          emptyMessage="No models"
        />
      </Card>

      {/* Cost by tenant table */}
      <SectionHeader
        title="Cost by Tenant"
        subtitle="Inference costs per tenant, sorted by spend"
      />
      <Card padding={false}>
        <Table
          columns={tenantColumns}
          data={sortedTenants as unknown as Record<string, unknown>[]}
          keyField="tenantId"
          emptyMessage="No tenants"
        />
      </Card>

      <SectionHeader
        title="Routing Reconciliation"
        subtitle="Requested vs actual model truth across tenant traffic"
      />
      <Card padding={false} className="mb-8">
        <Table
          columns={[
            {
              key: "tenantId",
              header: "Tenant",
              render: (row: ModelReconciliation["tenants"][0]) => (
                <span className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
                  {row.tenantId}
                </span>
              ),
            },
            {
              key: "divergenceRate",
              header: "Divergence",
              render: (row: ModelReconciliation["tenants"][0]) =>
                `${(row.divergenceRate * 100).toFixed(1)}%`,
            },
            {
              key: "unresolvedTruthRate",
              header: "Unresolved",
              render: (row: ModelReconciliation["tenants"][0]) =>
                `${(row.unresolvedTruthRate * 100).toFixed(1)}%`,
            },
            {
              key: "truthSources",
              header: "Truth Sources",
              render: (row: ModelReconciliation["tenants"][0]) =>
                Object.entries(row.truthSources)
                  .filter(([, count]) => Number(count) > 0)
                  .map(([source, count]) => `${source}:${count}`)
                  .join(" · ") || "n/a",
            },
            {
              key: "topPairs",
              header: "Top Requested → Actual",
              render: (row: ModelReconciliation["tenants"][0]) =>
                row.topPairs?.[0]
                  ? `${row.topPairs[0].requestedModel} -> ${row.topPairs[0].actualModel} (${row.topPairs[0].count})`
                  : "n/a",
            },
          ]}
          data={reconciliationTenants as unknown as Record<string, unknown>[]}
          keyField="tenantId"
          emptyMessage="No reconciliation data"
        />
      </Card>

      <SectionHeader
        title="Top Routing Pairs"
        subtitle="Most common requested vs actual model outcomes across the fleet"
      />
      <Card padding={false} className="mb-8">
        <Table
          columns={[
            { key: "requestedModel", header: "Requested" },
            { key: "actualModel", header: "Actual" },
            { key: "count", header: "Count" },
          ]}
          data={topDivergencePairs as unknown as Record<string, unknown>[]}
          keyField="pairId"
          emptyMessage="No routing pair data"
        />
      </Card>
    </div>
  );
}
